import { useMemo } from 'react';
import { Acc } from '../../../../components/edit-object/design/Accordion';
import { Field } from '../../../../components/edit-object/design/Field';
import { Input, Three } from '../../../../components/edit-object/design/Form';
import { AutocompleteFieldMultiSelectWithFavorites } from '../../../../components/AutocompleteFieldMultiSelectWithFavorites';
import { DatePickerField } from '../../../../components/DatePickerField';
import { StarredSelect } from '../../../../components/StarredSelect';
import { useSignatureData } from '../../../../hooks/useSignatureData';
import { filterDateLocations } from '../../../../utils/locationFilters';
import { en } from '../../../../translations/en';
import type { ArtworkDetailsForm } from '../useArtworkDetailsForm';

interface Props {
  form: ArtworkDetailsForm;
}

const DATE_TYPES: ReadonlyArray<string> =
  en.wizard.describe.steps.creationDate.creationDate.options;

export const CreationDateSection = ({ form }: Props) => {
  const { creationDate, setCreationDate } = form;

  const { signatureLocations, loading: locationsLoading } = useSignatureData();
  const dateLocations = useMemo(
    () => filterDateLocations(signatureLocations),
    [signatureLocations],
  );

  const dateTypeOptions = useMemo(() => {
    if (creationDate.type && !DATE_TYPES.includes(creationDate.type)) {
      return [...DATE_TYPES, creationDate.type];
    }
    return DATE_TYPES;
  }, [creationDate.type]);

  const update = (patch: Partial<typeof creationDate>) =>
    setCreationDate({ ...creationDate, ...patch });

  const summary = [creationDate.value, creationDate.display]
    .map((s) => (s ?? '').trim())
    .filter(Boolean)
    .join(' · ');

  return (
    <Acc
      id="sec-creation-date"
      title="Creation Date"
      summary={summary}
      emptyPlaceholder="No date set"
      defaultExpanded
    >
      <Three>
        <StarredSelect
          label="Date Type"
          options={dateTypeOptions}
          value={creationDate.type}
          onChange={(value) => update({ type: value })}
          variant="design"
        />
        <DatePickerField
          dateType={creationDate.type}
          value={creationDate.value}
          onChange={(value) => update({ value })}
          onBlur={form.flushSave}
          variant="design"
        />
        <AutocompleteFieldMultiSelectWithFavorites
          label="Where the date appears"
          value={creationDate.location || []}
          options={dateLocations}
          onChange={(value) => update({ location: value })}
          loading={locationsLoading}
          vocabularyType="date_location"
          variant="design"
        />
      </Three>

      <Field label="Date as displayed">
        <Input
          id="cd-display"
          value={creationDate.display}
          onChange={(e) => update({ display: e.target.value })}
          placeholder="e.g. '24, MCMXXIV, Spring 2024"
        />
      </Field>
    </Acc>
  );
};
